import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { listingsAPI } from "../api/index.js";
import { useAuth } from "../hooks/useAuth.jsx";
import SEO from "../components/SEO.jsx";

const G = {
  bg:"#0A0A0A", surface:"#111111", border:"#1E1E1E",
  text:"#E4E4DC", muted:"#666", accent:"#00C896",
  purple:"#7C6FFF", danger:"#FF6B6B", warn:"#FFB347",
  font:"'Sora', sans-serif", mono:"'DM Mono', monospace",
};

const COMPLAINT_TYPES = ["Withdrawal Issue","Fraud / Scam","Account Frozen","Fake Signals","Unauthorized Trades","Poor Customer Service","Other"];

const Stars = ({ rating, size=16, interactive=false, onChange }) => (
  <span>
    {[1,2,3,4,5].map(i => (
      <span key={i} onClick={() => interactive && onChange && onChange(i)}
        style={{ fontSize:size, color:i<=(rating||0)?"#FFB347":"#2A2A2A", cursor:interactive?"pointer":"default" }}>★</span>
    ))}
  </span>
);

const RegBadge = ({ status }) => {
  const colors = { regulated:G.accent, partial:G.warn, unregulated:G.danger, unverified:G.muted };
  const color = colors[status] || G.muted;
  return <span style={{ background:color+"22", border:`1px solid ${color}44`, color, fontSize:10, padding:"3px 10px", borderRadius:20, fontFamily:G.mono, letterSpacing:0.5 }}>{status?.toUpperCase()}</span>;
};

export default function ListingDetailPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData]           = useState(null);
  const [relatedQs, setRelatedQs] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [tab, setTab]             = useState("overview");
  const [toast, setToast]         = useState(null);

  const [rating, setRating]           = useState(0);
  const [reviewTitle, setReviewTitle] = useState("");
  const [reviewBody, setReviewBody]   = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);

  const [complaintType, setComplaintType] = useState("");
  const [complaintDesc, setComplaintDesc] = useState("");
  const [amountLost, setAmountLost]       = useState("");
  const [submittingComplaint, setSubmittingComplaint] = useState(false);

  const notify = (msg, type="success") => { setToast({msg,type}); setTimeout(()=>setToast(null),2500); };

  const load = async () => {
    try {
      const res = await listingsAPI.get(slug);
      setData(res.data);
      const qRes = await listingsAPI.questions(res.data.listing.id);
      setRelatedQs(qRes.data.questions);
    } catch { navigate("/listings"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [slug]);

  const handleReview = async () => {
    if (!rating) return notify("Please select a rating","error");
    if (reviewBody.trim().length < 20) return notify("Review must be at least 20 characters","error");
    setSubmittingReview(true);
    try {
      await listingsAPI.review(data.listing.id, { rating, title:reviewTitle, body:reviewBody });
      setRating(0); setReviewTitle(""); setReviewBody("");
      notify("Review posted!"); load();
    } catch (e) { notify(e.response?.data?.errors?.[0]?.msg||"Failed","error"); }
    finally { setSubmittingReview(false); }
  };

  const handleComplaint = async () => {
    if (!complaintType) return notify("Please select a complaint type","error");
    if (complaintDesc.trim().length < 20) return notify("Description must be at least 20 characters","error");
    setSubmittingComplaint(true);
    try {
      await listingsAPI.complaint(data.listing.id, { type:complaintType, description:complaintDesc, amount_lost:amountLost||undefined });
      setComplaintType(""); setComplaintDesc(""); setAmountLost("");
      notify("Complaint submitted!"); load();
    } catch (e) { notify(e.response?.data?.error||"Failed","error"); }
    finally { setSubmittingComplaint(false); }
  };

  if (loading) return <div style={{ textAlign:"center", padding:"80px 0", color:G.muted, fontFamily:G.font }}>Loading...</div>;
  if (!data) return null;

  const { listing:l, reviews, complaints } = data;
  const avgRating = l.avg_rating ? Number(l.avg_rating).toFixed(1) : null;

  const listingJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": l.name,
    "description": l.description,
    "url": l.website || `https://askfogra.com/listings/${l.slug}`,
    "sameAs": l.website ? [l.website] : [],
    ...(avgRating && l.review_count > 0 && {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": avgRating,
        "reviewCount": l.review_count,
        "bestRating": "5",
        "worstRating": "1"
      }
    }),
    ...(reviews.length > 0 && {
      "review": reviews.slice(0, 5).map(r => ({
        "@type": "Review",
        "reviewRating": { "@type": "Rating", "ratingValue": r.rating, "bestRating": "5" },
        "name": r.title || `${l.name} review`,
        "reviewBody": r.body,
        "datePublished": r.created_at,
        "author": { "@type": "Person", "name": r.author_username }
      }))
    })
  };

  return (
    <div style={{ maxWidth:900, margin:"0 auto", padding:"28px 16px", fontFamily:G.font }}>
      <SEO
        title={`${l.name} — Reviews & Complaints`}
        description={l.description ? `${l.description.slice(0, 140)} — Read real reviews and complaints on askFOGRA.` : `Read real reviews and complaints about ${l.name} on askFOGRA.`}
        url={`/listings/${l.slug}`}
        jsonLd={listingJsonLd}
      />
      {toast && <div style={{ position:"fixed", top:20, right:20, zIndex:9999, background:toast.type==="error"?G.danger:G.accent, color:toast.type==="error"?"#fff":"#000", padding:"11px 20px", borderRadius:8, fontFamily:G.mono, fontSize:13, fontWeight:600 }}>{toast.msg}</div>}

      <Link to="/listings" style={{ textDecoration:"none", color:G.muted, fontSize:13, fontFamily:G.mono }}>← Back to Listings</Link>

      {/* Header */}
      <div style={{ background:G.surface, border:`1px solid ${l.is_flagged?G.danger:G.border}`, borderRadius:16, padding:28, margin:"18px 0" }}>
        {l.is_flagged && (
          <div style={{ background:G.danger+"15", border:`1px solid ${G.danger}44`, borderRadius:8, padding:"10px 16px", marginBottom:16, fontSize:13, color:G.danger }}>
            ⚠️ This listing has been flagged for suspicious activity. Exercise extreme caution.
          </div>
        )}
        <div style={{ display:"flex", gap:20, flexWrap:"wrap" }}>
          <div style={{ width:64, height:64, borderRadius:14, background:"#1A1A1A", border:`1px solid #2A2A2A`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:28, fontWeight:700, color:G.accent, fontFamily:G.mono, flexShrink:0 }}>
            {l.name.slice(0,1)}
          </div>
          <div style={{ flex:1 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", marginBottom:6 }}>
              <h1 style={{ fontSize:24, fontWeight:700, color:G.text, margin:0 }}>{l.name}</h1>
              {l.is_verified && <span style={{ background:G.accent+"22", border:`1px solid ${G.accent}44`, color:G.accent, fontSize:10, padding:"2px 8px", borderRadius:20, fontFamily:G.mono }}>✓ VERIFIED</span>}
              <RegBadge status={l.regulation_status} />
            </div>
            {l.description && <p style={{ color:G.muted, fontSize:14, lineHeight:1.6, marginBottom:12 }}>{l.description}</p>}
            {avgRating && (
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <Stars rating={avgRating} />
                <span style={{ fontSize:20, fontWeight:700, color:G.warn, fontFamily:G.mono }}>{avgRating}</span>
                <span style={{ fontSize:12, color:G.muted }}>({l.review_count} reviews)</span>
              </div>
            )}
          </div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:12, marginTop:20, paddingTop:20, borderTop:`1px solid ${G.border}` }}>
          {l.category && <div><div style={{ fontSize:10, color:G.muted, fontFamily:G.mono, letterSpacing:1, marginBottom:4 }}>CATEGORY</div><div style={{ fontSize:13, color:G.text }}>{l.category}</div></div>}
          {l.country && <div><div style={{ fontSize:10, color:G.muted, fontFamily:G.mono, letterSpacing:1, marginBottom:4 }}>COUNTRY</div><div style={{ fontSize:13, color:G.text }}>{l.country}</div></div>}
          {l.founded_year && <div><div style={{ fontSize:10, color:G.muted, fontFamily:G.mono, letterSpacing:1, marginBottom:4 }}>FOUNDED</div><div style={{ fontSize:13, color:G.text }}>{l.founded_year}</div></div>}
          {l.regulator && <div><div style={{ fontSize:10, color:G.muted, fontFamily:G.mono, letterSpacing:1, marginBottom:4 }}>REGULATOR</div><div style={{ fontSize:13, color:G.text }}>{l.regulator}</div></div>}
          {l.website && <div><div style={{ fontSize:10, color:G.muted, fontFamily:G.mono, letterSpacing:1, marginBottom:4 }}>WEBSITE</div><a href={l.website} target="_blank" rel="noopener noreferrer" style={{ fontSize:13, color:G.accent, textDecoration:"none" }}>Visit →</a></div>}
          <div><div style={{ fontSize:10, color:G.muted, fontFamily:G.mono, letterSpacing:1, marginBottom:4 }}>COMPLAINTS</div><div style={{ fontSize:13, color:complaints.length>0?G.danger:G.accent }}>{complaints.length} reported</div></div>
        </div>
      </div>

      {/* Tabs */}
      <div className="listing-tabs" style={{ display:"flex", gap:4, marginBottom:20, background:G.surface, border:`1px solid ${G.border}`, borderRadius:10, padding:4, width:"fit-content" }}>
        {[["overview","Overview"],["reviews",`Reviews (${reviews.length})`],["complaints",`Complaints (${complaints.length})`],["questions",`Questions (${relatedQs.length})`]].map(([t,label]) => (
          <button key={t} onClick={() => setTab(t)} style={{
            background:tab===t?"#1A1A1A":"transparent",
            border:tab===t?`1px solid #2A2A2A`:"1px solid transparent",
            color:tab===t?G.accent:G.muted,
            padding:"6px 16px", borderRadius:7, cursor:"pointer",
            fontSize:13, fontFamily:G.mono, transition:"all 0.15s"
          }}>{label}</button>
        ))}
      </div>

      {/* Overview Tab */}
      {tab === "overview" && (
        <div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:20 }}>
            <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:12, padding:20 }}>
              <div style={{ fontSize:11, color:G.muted, fontFamily:G.mono, letterSpacing:1, textTransform:"uppercase", marginBottom:14 }}>Rating Breakdown</div>
              {avgRating ? (
                <div style={{ textAlign:"center" }}>
                  <div style={{ fontSize:48, fontWeight:700, color:G.warn, fontFamily:G.mono }}>{avgRating}</div>
                  <Stars rating={avgRating} size={20} />
                  <div style={{ fontSize:12, color:G.muted, marginTop:6 }}>Based on {l.review_count} reviews</div>
                </div>
              ) : <div style={{ color:G.muted, fontSize:13 }}>No reviews yet</div>}
            </div>
            <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:12, padding:20 }}>
              <div style={{ fontSize:11, color:G.muted, fontFamily:G.mono, letterSpacing:1, textTransform:"uppercase", marginBottom:14 }}>Complaint Summary</div>
              {complaints.length > 0 ? (
                <div>
                  <div style={{ fontSize:32, fontWeight:700, color:G.danger, fontFamily:G.mono }}>{complaints.length}</div>
                  <div style={{ fontSize:13, color:G.muted, marginTop:4 }}>complaints filed</div>
                  {complaints.slice(0,2).map(c => (
                    <div key={c.id} style={{ marginTop:10, padding:"8px 12px", background:"#0D0D0D", borderRadius:8, fontSize:12, color:G.muted }}>
                      <span style={{ color:G.danger }}>⚠ </span>{c.type}
                      {c.amount_lost && <span style={{ color:G.danger, fontFamily:G.mono }}> — ${c.amount_lost}</span>}
                    </div>
                  ))}
                </div>
              ) : <div style={{ color:G.accent, fontSize:13 }}>✓ No complaints filed</div>}
            </div>
          </div>

          {/* Related Questions */}
          <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:12, padding:20 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
              <div style={{ fontSize:11, color:G.muted, fontFamily:G.mono, letterSpacing:1, textTransform:"uppercase" }}>Related Questions</div>
              <Link to={`/ask`} style={{ fontSize:12, color:G.accent, textDecoration:"none", fontFamily:G.mono }}>+ Ask a question</Link>
            </div>
            {relatedQs.length === 0 ? (
              <div style={{ color:"#333", fontSize:13, textAlign:"center", padding:"20px 0" }}>No questions linked to this listing yet</div>
            ) : relatedQs.map(q => (
              <Link key={q.id} to={`/q/${q.slug}`} style={{ textDecoration:"none" }}>
                <div style={{ padding:"10px 0", borderBottom:`1px solid ${G.border}`, transition:"opacity 0.15s" }}
                  onMouseOver={e => e.currentTarget.style.opacity="0.7"}
                  onMouseOut={e => e.currentTarget.style.opacity="1"}>
                  <div style={{ fontSize:14, color:G.text, fontWeight:500, marginBottom:4 }}>{q.title}</div>
                  <div style={{ display:"flex", gap:12 }}>
                    <span style={{ fontSize:11, color:G.muted, fontFamily:G.mono }}>▲ {q.vote_score}</span>
                    <span style={{ fontSize:11, color:G.muted, fontFamily:G.mono }}>💬 {q.answer_count}</span>
                    <span style={{ fontSize:11, color:"#3A3A3A", fontFamily:G.mono }}>{new Date(q.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Reviews Tab */}
      {tab === "reviews" && (
        <div>
          {reviews.map(r => (
            <div key={r.id} style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:12, padding:20, marginBottom:12 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8, flexWrap:"wrap" }}>
                <Stars rating={r.rating} />
                <span style={{ fontSize:13, fontWeight:600, color:G.text }}>{r.title}</span>
                {r.is_verified && <span style={{ fontSize:10, color:G.accent, fontFamily:G.mono }}>✓ VERIFIED</span>}
              </div>
              <p style={{ fontSize:14, color:"#CCC", lineHeight:1.7, marginBottom:10 }}>{r.body}</p>
              <div style={{ fontSize:12, color:G.muted, fontFamily:G.mono }}>{r.author_username} · {new Date(r.created_at).toLocaleDateString()}</div>
            </div>
          ))}
          {reviews.length === 0 && <div style={{ textAlign:"center", padding:"40px 0", color:"#333" }}>No reviews yet — be the first!</div>}
          {user ? (
            <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:12, padding:22, marginTop:20 }}>
              <div style={{ fontSize:12, color:G.muted, fontFamily:G.mono, letterSpacing:1, textTransform:"uppercase", marginBottom:16 }}>Write a Review</div>
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:11, color:G.muted, marginBottom:6 }}>Rating *</div>
                <Stars rating={rating} size={28} interactive onChange={setRating} />
              </div>
              <input placeholder="Review title (optional)" value={reviewTitle} onChange={e => setReviewTitle(e.target.value)}
                style={{ width:"100%", background:"#0D0D0D", border:`1px solid #2A2A2A`, color:G.text, borderRadius:8, padding:"10px 14px", fontSize:14, fontFamily:G.font, outline:"none", boxSizing:"border-box", marginBottom:10 }} />
              <textarea value={reviewBody} onChange={e => setReviewBody(e.target.value)} rows={4}
                placeholder="Share your experience. Minimum 20 characters."
                style={{ width:"100%", background:"#0D0D0D", border:`1px solid #2A2A2A`, color:G.text, borderRadius:8, padding:"10px 14px", fontSize:14, fontFamily:G.font, resize:"vertical", outline:"none", lineHeight:1.6, boxSizing:"border-box" }} />
              <div style={{ display:"flex", justifyContent:"flex-end", marginTop:12 }}>
                <button onClick={handleReview} disabled={submittingReview||!rating||reviewBody.trim().length<20}
                  style={{ background:G.accent, border:"none", color:"#000", padding:"9px 22px", borderRadius:20, cursor:"pointer", fontFamily:G.mono, fontWeight:600, fontSize:13, opacity:submittingReview||!rating||reviewBody.trim().length<20?0.4:1 }}>
                  {submittingReview?"Posting...":"Post Review →"}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ textAlign:"center", padding:"20px 0", color:G.muted, fontSize:14 }}>
              <Link to="/login" style={{ color:G.accent }}>Login</Link> to write a review
            </div>
          )}
        </div>
      )}

      {/* Complaints Tab */}
      {tab === "complaints" && (
        <div>
          {complaints.map(c => (
            <div key={c.id} style={{ background:G.surface, border:`1px solid ${G.danger}44`, borderRadius:12, padding:20, marginBottom:12 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8, flexWrap:"wrap" }}>
                <span style={{ background:G.danger+"22", border:`1px solid ${G.danger}44`, color:G.danger, fontSize:11, padding:"2px 10px", borderRadius:20, fontFamily:G.mono }}>⚠ {c.type}</span>
                {c.amount_lost && <span style={{ fontSize:13, color:G.danger, fontFamily:G.mono, fontWeight:700 }}>Lost: {c.currency} {Number(c.amount_lost).toLocaleString()}</span>}
                <span style={{ fontSize:11, color:G.muted, fontFamily:G.mono, marginLeft:"auto" }}>{c.status?.toUpperCase()}</span>
              </div>
              <p style={{ fontSize:14, color:"#CCC", lineHeight:1.7, marginBottom:10 }}>{c.description}</p>
              <div style={{ fontSize:12, color:G.muted, fontFamily:G.mono }}>{c.author_username} · {new Date(c.created_at).toLocaleDateString()}</div>
            </div>
          ))}
          {complaints.length === 0 && <div style={{ textAlign:"center", padding:"40px 0", color:G.accent }}>✓ No complaints filed for this listing</div>}
          {user ? (
            <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:12, padding:22, marginTop:20 }}>
              <div style={{ fontSize:12, color:G.muted, fontFamily:G.mono, letterSpacing:1, textTransform:"uppercase", marginBottom:16 }}>File a Complaint</div>
              <select value={complaintType} onChange={e => setComplaintType(e.target.value)}
                style={{ width:"100%", background:"#0D0D0D", border:`1px solid #2A2A2A`, color:complaintType?G.text:G.muted, borderRadius:8, padding:"10px 14px", fontSize:14, fontFamily:G.font, outline:"none", cursor:"pointer", marginBottom:10 }}>
                <option value="">Select complaint type *</option>
                {COMPLAINT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <input placeholder="Amount lost in USD (optional)" value={amountLost} onChange={e => setAmountLost(e.target.value)} type="number"
                style={{ width:"100%", background:"#0D0D0D", border:`1px solid #2A2A2A`, color:G.text, borderRadius:8, padding:"10px 14px", fontSize:14, fontFamily:G.font, outline:"none", boxSizing:"border-box", marginBottom:10 }} />
              <textarea value={complaintDesc} onChange={e => setComplaintDesc(e.target.value)} rows={4}
                placeholder="Describe what happened. When? How much? Minimum 20 characters."
                style={{ width:"100%", background:"#0D0D0D", border:`1px solid #2A2A2A`, color:G.text, borderRadius:8, padding:"10px 14px", fontSize:14, fontFamily:G.font, resize:"vertical", outline:"none", lineHeight:1.6, boxSizing:"border-box" }} />
              <div style={{ display:"flex", justifyContent:"flex-end", marginTop:12 }}>
                <button onClick={handleComplaint} disabled={submittingComplaint||!complaintType||complaintDesc.trim().length<20}
                  style={{ background:G.danger, border:"none", color:"#fff", padding:"9px 22px", borderRadius:20, cursor:"pointer", fontFamily:G.mono, fontWeight:600, fontSize:13, opacity:submittingComplaint||!complaintType||complaintDesc.trim().length<20?0.4:1 }}>
                  {submittingComplaint?"Submitting...":"Submit Complaint →"}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ textAlign:"center", padding:"20px 0", color:G.muted, fontSize:14 }}>
              <Link to="/login" style={{ color:G.accent }}>Login</Link> to file a complaint
            </div>
          )}
        </div>
      )}

      {/* Questions Tab */}
      {tab === "questions" && (
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <div style={{ fontSize:13, color:G.muted }}>{relatedQs.length} questions linked to {l.name}</div>
            <Link to="/ask" style={{ fontSize:12, color:G.accent, textDecoration:"none", fontFamily:G.mono, background:G.accent+"15", border:`1px solid ${G.accent}44`, padding:"5px 14px", borderRadius:20 }}>+ Ask a Question</Link>
          </div>
          {relatedQs.length === 0 ? (
            <div style={{ textAlign:"center", padding:"60px 0", color:"#333" }}>
              <div style={{ fontSize:32, marginBottom:10 }}>❓</div>
              <div style={{ fontSize:14, marginBottom:12 }}>No questions linked to this listing yet</div>
              <Link to="/ask" style={{ color:G.accent, fontSize:13 }}>Be the first to ask →</Link>
            </div>
          ) : relatedQs.map(q => (
            <Link key={q.id} to={`/q/${q.slug}`} style={{ textDecoration:"none" }}>
              <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:10, padding:"14px 18px", marginBottom:8, transition:"border-color 0.15s" }}
                onMouseOver={e => e.currentTarget.style.borderColor="#2A2A2A"}
                onMouseOut={e => e.currentTarget.style.borderColor=G.border}>
                <div style={{ fontSize:14, fontWeight:600, color:G.text, marginBottom:6, lineHeight:1.4 }}>{q.title}</div>
                <div style={{ display:"flex", gap:14 }}>
                  <span style={{ fontSize:11, color:G.muted, fontFamily:G.mono }}>▲ {q.vote_score}</span>
                  <span style={{ fontSize:11, color:G.muted, fontFamily:G.mono }}>💬 {q.answer_count}</span>
                  <span style={{ fontSize:11, color:"#3A3A3A", fontFamily:G.mono }}>{q.author_username}</span>
                  <span style={{ fontSize:11, color:"#3A3A3A", fontFamily:G.mono }}>{new Date(q.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
